﻿import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/index';
import {
  filterMessageRecipients,
  messageRosterHouseIds,
} from '../core/rules/messagingAccess';
import { isDemoAuthEnabled } from '../app/demoAuth';
import { DEMO_ACCOUNTS } from '../app/demoCredentials';
import type { IDataAdapter } from '../adapters/data/IDataAdapter';

function emailForUser(user: { id?: string; role?: string; name?: string }) {
  if (!isDemoAuthEnabled()) return undefined;
  const byRole = DEMO_ACCOUNTS.find((a) => a.role === user.role && a.name === user.name);
  if (byRole?.email) return byRole.email;
  if (user.role === 'lister' && user.name === 'Ramón V.') {
    return DEMO_ACCOUNTS.find((a) => a.role === 'lister')?.email;
  }
  if (user.role === 'banker') {
    return DEMO_ACCOUNTS.find((a) => a.role === 'banker')?.email;
  }
  if (user.role === 'runner') {
    return DEMO_ACCOUNTS.find((a) => a.role === 'runner')?.email;
  }
  return undefined;
}

type RosterUser = Record<string, unknown> & {
  email?: string;
  role?: string;
  name?: string;
  alias?: string;
  id?: string;
  houseId?: string;
  houseIds?: string[];
};

export function useMessagingRecipients(adapter: IDataAdapter | null | undefined) {
  const { user } = useAuth();
  const rosterHouseIds = useMemo(
    () => messageRosterHouseIds(user),
    [user],
  );
  const rosterKey = rosterHouseIds.join(',');
  const [users, setUsers] = useState<RosterUser[]>([]);

  useEffect(() => {
    if (!adapter || rosterHouseIds.length === 0) {
      setUsers([]);
      return undefined;
    }

    if (adapter.subscribeMessagingRoster) {
      return adapter.subscribeMessagingRoster(rosterHouseIds, (rows) => {
        setUsers(rows as RosterUser[]);
      });
    }

    // Fallback: active-house directory only (never unscoped platform list).
    return adapter.subscribe<RosterUser[]>('users', (rows) => {
      setUsers(Array.isArray(rows) ? rows : []);
    });
  }, [adapter, rosterKey, rosterHouseIds]);

  return useMemo(() => {
    const enriched = users.map((u) => ({
      ...u,
      email: u.email ?? emailForUser(u),
      alias: typeof u.alias === 'string' ? u.alias : undefined,
    }));
    return filterMessageRecipients(user, enriched);
  }, [users, user]);
}
